import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Annonce, AnnonceFormData } from '../models/annonce.model';
import { PaginatedResponse, PaginationParams } from '../models/pagination.model';

@Injectable({
  providedIn: 'root'
})
export class AnnoncenewService {
  private apiUrl = 'https://app.titv-store-api.com/annonces';

  constructor(private http: HttpClient) { }

  // ✅ MÊME MÉTHODE QUE MAGAZINE SERVICE
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  // ✅ MÊME MÉTHODE QUE MAGAZINE SERVICE
  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  // ✅ MÊME MÉTHODE QUE MAGAZINE SERVICE - MAIS AVEC FORM_DATA POUR UPLOAD
  private getFormDataHeaders(): HttpHeaders {
    const token = this.getToken();
    
    if (token) {
      // Note: Ne pas mettre 'Content-Type' pour FormData - il sera automatique
      return new HttpHeaders({
        'Authorization': 'Bearer ' + token
      });
    }
    
    return new HttpHeaders();
  }

  // ✅ MÊME MÉTHODE QUE MAGAZINE SERVICE
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    
    console.error('Erreur AnnoncenewService:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // Get all annonces
  getAllAnnonces(): Observable<Annonce[]> {
    const headers = this.getHeaders();
    
    return this.http.get<Annonce[]>(`${this.apiUrl}`, { headers })
      .pipe(catchError(this.handleError));
  }

  // Get paginated annonces
  getPaginatedAnnonces(params: PaginationParams): Observable<PaginatedResponse<Annonce>> {
    const headers = this.getHeaders();
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('size', params.size.toString());

    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }

    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }

    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }

    if (params.ville) {
      httpParams = httpParams.set('ville', params.ville);
    }

    if (params.typeAnnonce) {
      httpParams = httpParams.set('typeAnnonce', params.typeAnnonce);
    }

    if (params.isApproved !== undefined) {
      httpParams = httpParams.set('isApproved', params.isApproved.toString());
    }

    return this.http.get<PaginatedResponse<Annonce>>(`${this.apiUrl}/paginated`, { 
      headers, 
      params: httpParams 
    }).pipe(catchError(this.handleError));
  }

  // Get annonce by ID
  getAnnonceById(id: number): Observable<Annonce> {
    const headers = this.getHeaders();
    
    return this.http.get<Annonce>(`${this.apiUrl}/${id}`, { headers })
      .pipe(catchError(this.handleError));
  }

  // Create new annonce
  createAnnonce(annonceData: AnnonceFormData): Observable<Annonce> {
    const headers = this.getHeaders();
    
    return this.http.post<Annonce>(`${this.apiUrl}`, annonceData, { headers })
      .pipe(catchError(this.handleError));
  }

  // Update annonce
  updateAnnonce(id: number, annonceData: AnnonceFormData): Observable<Annonce> {
    const headers = this.getHeaders();
    
    return this.http.put<Annonce>(`${this.apiUrl}/${id}`, annonceData, { headers })
      .pipe(catchError(this.handleError));
  }

  // Delete annonce
  deleteAnnonce(id: number): Observable<void> {
    const headers = this.getHeaders();
    
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers })
      .pipe(catchError(this.handleError));
  }

  // Approve/Disapprove annonce
  updateApprovalStatus(id: number, isApproved: boolean): Observable<Annonce> {
    const headers = this.getHeaders();
    
    return this.http.put<Annonce>(`${this.apiUrl}/${id}/approve`, { isApproved }, { headers })
      .pipe(catchError(this.handleError));
  }

  // Get annonces by approval status
  getAnnoncesByApprovalStatus(isApproved: boolean): Observable<Annonce[]> {
    const headers = this.getHeaders();
    
    return this.http.get<Annonce[]>(`${this.apiUrl}/approved/${isApproved}`, { headers })
      .pipe(catchError(this.handleError));
  }

  // Get paginated annonces by approval status
  getPaginatedByApprovalStatus(isApproved: boolean, page: number = 0, size: number = 10): Observable<PaginatedResponse<Annonce>> {
    const headers = this.getHeaders();
    const params = new HttpParams()
      .set('isApproved', isApproved.toString())
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<PaginatedResponse<Annonce>>(`${this.apiUrl}/paginated`, { 
      headers, 
      params 
    }).pipe(catchError(this.handleError));
  }

  // Search annonces with optional filters
  searchAnnonces(searchTerm: string, filters?: {
    ville?: string;
    typeAnnonce?: string;
    isApproved?: boolean;
  }): Observable<Annonce[]> {
    const headers = this.getHeaders();
    let params = new HttpParams().set('search', searchTerm);

    if (filters) {
      if (filters.ville) {
        params = params.set('ville', filters.ville);
      }
      if (filters.typeAnnonce) {
        params = params.set('typeAnnonce', filters.typeAnnonce);
      }
      if (filters.isApproved !== undefined) {
        params = params.set('isApproved', filters.isApproved.toString());
      }
    }

    return this.http.get<Annonce[]>(`${this.apiUrl}/search`, { 
      headers, 
      params 
    }).pipe(catchError(this.handleError));
  }

  // Increment view count
  incrementViewCount(id: number): Observable<void> {
    const headers = this.getHeaders();
    
    return this.http.put<void>(`${this.apiUrl}/${id}/increment-view`, {}, { headers })
      .pipe(catchError(this.handleError));
  }

  // Upload images for annonce - CORRECTION: Utiliser FormData sans Content-Type
  uploadImages(id: number, images: File[]): Observable<{ imageUrls: string[] }> {
    const headers = this.getFormDataHeaders();
    const formData = new FormData();
    
    images.forEach(image => {
      formData.append('images', image, image.name);
    });

    return this.http.post<{ imageUrls: string[] }>(
      `${this.apiUrl}/${id}/upload-images`, 
      formData, 
      { headers }
    ).pipe(catchError(this.handleError));
  }

  // Get annonces by ville
  getAnnoncesByVille(ville: string): Observable<Annonce[]> {
    const headers = this.getHeaders();
    
    return this.http.get<Annonce[]>(`${this.apiUrl}/ville/${ville}`, { headers })
      .pipe(catchError(this.handleError));
  }

  // Get annonces by type
  getAnnoncesByType(typeAnnonce: string): Observable<Annonce[]> {
    const headers = this.getHeaders();
    
    return this.http.get<Annonce[]>(`${this.apiUrl}/type/${typeAnnonce}`, { headers })
      .pipe(catchError(this.handleError));
  }

  // Get statistics (optional)
  getStatistics(): Observable<{
    total: number;
    approved: number;
    pending: number;
    totalViews: number;
    byVille: { ville: string; count: number }[];
    byType: { type: string; count: number }[];
  }> {
    const headers = this.getHeaders();
    
    return this.http.get<{
      total: number;
      approved: number;
      pending: number;
      totalViews: number;
      byVille: { ville: string; count: number }[];
      byType: { type: string; count: number }[];
    }>(`${this.apiUrl}/statistics`, { headers })
      .pipe(catchError(this.handleError));
  }
}